import { castArray, compact, isEmpty } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import { MayBeArray } from 'app/@types/utils/may-be-array';

interface Options {
    selector: string;
    enabled?: boolean;
    /** Must be referentially stable */
    attribute?: MayBeArray<string>;
    target?: Node;
    debounce?: number;
}

export default function useQuerySelector<E extends Element>(options: Options) {
    const { selector, enabled = true, attribute, target, debounce = 0 } = options;

    const query = useCallback((selector: string) => {
        const node = document.querySelector(selector);
        return node instanceof Element ? (node as E) : null;
    }, []);

    const [element, setElement] = useState<E | null>(() => query(options.selector));

    const timerRef = useRef<ReturnType<typeof window.setTimeout>>();

    useEffect(() => {
        clearTimeout(timerRef.current);

        if (enabled) {
            setElement(query(selector));

            const observer = new MutationObserver(() => {
                clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    setElement(query(selector));
                }, debounce);
            });

            const attributeFilter = compact(castArray(attribute));

            observer.observe(target ?? document.body, {
                childList: true,
                subtree: true,
                attributeFilter: isEmpty(attributeFilter) ? undefined : attributeFilter
            });

            return () => {
                clearTimeout(timerRef.current);
                observer.disconnect();
            };
        } else {
            setElement(null);
        }
    }, [enabled, attribute, selector, target, query, debounce]);

    return element;
}
